<template>
  <div class="pagination">
    <div class="p-header">
      <fn-divider>
        {{ $t('pagination.head', { currentPage: realCurrentPage, totalPages }) }}
      </fn-divider>
    </div>

    <div class="p-items">
      <a
        v-if="allowJumpToStartEnd && realCurrentPage > 2"
        id="pagination-firstPage"
        :href="getPaginationHref()"
        class="p-item p-item--border"
        @click.prevent="$emit('goToPage', 1)"
      >
        <fn-icon
          icon="double-arrow-left"
          size="xxs"
        />
      </a>
      <a
        v-if="realCurrentPage > 1"
        id="pagination-previousPage"
        :href="getPaginationHref(currentPage)"
        :aria-label="$t('accessibility.pagination.previous')"
        class="p-item p-item--border"
        @click.prevent="$emit('goToPage', realCurrentPage - 1)"
      >
        <fn-icon
          icon="arrow-left"
          size="xxs"
        />
      </a>

      <a
        v-for="page in pages"
        :id="`pagination-page${page}`"
        :key="page"
        :class="{
          'p-item--active': page === realCurrentPage,
        }"
        :href="getPaginationHref(page)"
        :aria-label="$t('accessibility.pagination.page', { page })"
        class="p-item"
        @click.prevent="$emit('goToPage', page)"
        >{{ page }}</a
      >

      <a
        v-if="realCurrentPage < totalPages"
        id="pagination-nextPage"
        :href="getPaginationHref(realCurrentPage + 1)"
        :aria-label="$t('accessibility.pagination.next')"
        class="p-item p-item--border"
        @click.prevent="$emit('goToPage', realCurrentPage + 1)"
      >
        <fn-icon
          icon="arrow-right"
          size="xxs"
        />
      </a>
      <a
        v-if="allowJumpToStartEnd && realCurrentPage < totalPages - 1"
        id="pagination-lastPage"
        :href="getPaginationHref(totalPages)"
        :aria-label="$t('accessibility.pagination.last')"
        class="p-item p-item--border"
        @click.prevent="$emit('goToPage', totalPages - 1)"
      >
        <fn-icon
          icon="double-arrow-right"
          size="xxs"
        />
      </a>
    </div>
  </div>
</template>

<script>
export default defineComponent({
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true,
    },
    allowJumpToStartEnd: {
      type: Boolean,
      default: false,
    },
    productListingPageActivated: {
      type: Boolean,
    },
    queryParameterKey: {
      type: String,
      default: 'currentPage',
    },
  },
  emits: ['goToPage'],
  computed: {
    realCurrentPage() {
      return this.currentPage + 1;
    },

    pages() {
      const pages = [];

      if (this.realCurrentPage > 1) {
        if (this.realCurrentPage > 4) {
          pages.push(this.realCurrentPage - 4);
        }

        if (this.realCurrentPage > 3) {
          pages.push(this.realCurrentPage - 3);
        }

        if (this.realCurrentPage > 2) {
          pages.push(this.realCurrentPage - 2);
        }

        pages.push(this.realCurrentPage - 1);
      }

      pages.push(this.realCurrentPage);

      if (this.realCurrentPage < this.totalPages) {
        pages.push(this.realCurrentPage + 1);

        if (this.realCurrentPage + 2 <= this.totalPages) {
          pages.push(this.realCurrentPage + 2);
        }

        if (this.realCurrentPage + 3 <= this.totalPages) {
          pages.push(this.realCurrentPage + 3);
        }

        if (this.realCurrentPage + 4 <= this.totalPages) {
          pages.push(this.realCurrentPage + 4);
        }
      }

      return pages;
    },
  },
  methods: {
    getPaginationHref(page) {
      const urlSearchParams = new URLSearchParams();

      if (this.productListingPageActivated && this.$route.query.q) {
        urlSearchParams.append('q', this.$route.query.q);
      }

      if (urlSearchParams.has(this.queryParameterKey)) {
        urlSearchParams.delete(this.queryParameterKey);
      }

      if (page && page !== 1) {
        urlSearchParams.append(this.queryParameterKey, page);
      }

      const params = urlSearchParams.toString();

      return `${this.$route.path}${params ? `?${params}` : ''}`;
    },
  },
});
</script>

<style lang="scss" scoped>
.pagination {
  position: relative;
  display: block;

  .p-header {
    position: relative;
    z-index: -1;
    margin-bottom: $pad;
  }

  .p-head {
    background-color: $color-general-background-neutral-default;
    color: palette(grey);
    padding: 0 $pad;
    z-index: 1;
    font-size: map-get($font-sizes, 'm');
  }

  .p-items {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .p-item {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: $ui-height;
    height: $ui-height;
    margin-right: math.div($pad, 2);
    color: palette(black);
    border-radius: map-get($border-radiuses, 'l');
    text-decoration: none;

    &:last-child {
      margin-right: 0;
    }

    &:hover,
    &:focus,
    &.p-item--active {
      outline: 0;
      background-color: palette(sand, light);
    }

    &:focus-visible {
      @include focus-outline-default;
      outline-color: palette(grey, light);
    }

    &.p-item--active {
      pointer-events: none;
    }

    &.p-item--border {
      border: 1px solid palette(grey, light);
    }
  }
}
</style>
